/**
 * HTTP helpers for the shipping hub page (see ShippingHubPage.tsx).
 */
import { apiFetch } from "@/lib/api";
import { SHIPPING_LABOR_PAGE } from "@/lib/labor/laborPageKey";

import type {
  LabelImage,
  PackingSlipRowsResponse,
  PackingValue,
  ShipmentPackageDetail,
  ShipmentRow,
  ShippingCarrierRow,
  TransferRow,
  UpsLabelData,
  ZplPrinter,
} from "./shippingHubTypes";

export const SHIPMENTS_QUERY_KEY = ["shipping", "shipments"] as const;
export const TRANSFERS_QUERY_KEY = ["shipping", "transfers"] as const;
export const CARRIERS_QUERY_KEY = ["shipping", "carriers"] as const;

export function shipmentPackageDetailsQueryKey(shipmentId: number) {
  return ["shipping", "package-details", shipmentId] as const;
}

export async function fetchShipments(): Promise<ShipmentRow[]> {
  const data = await apiFetch<{ shipments: ShipmentRow[] }>("/api/shipping/shipments");
  return data.shipments;
}

export async function fetchCarriers(): Promise<ShippingCarrierRow[]> {
  const data = await apiFetch<{ carriers: ShippingCarrierRow[] }>("/api/shipping/carriers");
  return data.carriers;
}

export async function toggleReady(shipmentId: number): Promise<{ ready_for_pickup: boolean }> {
  return apiFetch(`/api/shipping/shipments/${shipmentId}/toggle-ready`, { method: "POST" });
}

export async function markShipped(shipmentId: number, carrier: string, tracking: string): Promise<void> {
  await apiFetch(`/api/shipping/shipments/${shipmentId}/mark-shipped`, {
    method: "POST",
    body: { carrier, tracking },
  });
}

export async function toggleTransferReady(transferId: number): Promise<{ ready_for_pickup: boolean }> {
  return apiFetch(`/api/shipping/transfers/${String(transferId)}/toggle-ready`, { method: "POST" });
}

export async function markTransferSent(
  transferId: number,
  carrier: string | null,
  tracking: string | null,
  options?: { requireReadyForPickup?: boolean },
): Promise<void> {
  await apiFetch(`/api/shipping/transfers/${String(transferId)}/mark-sent`, {
    method: "POST",
    body: {
      carrier: carrier || undefined,
      tracking: tracking || undefined,
      require_ready_for_pickup: options?.requireReadyForPickup,
    },
  });
}

export async function fetchTransferPackingSlipRows(transferId: number): Promise<PackingSlipRowsResponse> {
  return apiFetch<PackingSlipRowsResponse>(`/api/shipping/transfers/${String(transferId)}/packing-slip-rows`);
}

export async function generateTransferPackingSlipPdf(
  transferId: number,
  isPallet: boolean,
  packingValues: PackingValue[],
): Promise<string> {
  const data = await apiFetch<{ pdfBase64: string }>("/api/shipping/transfers/packing-slip-pdf", {
    method: "POST",
    body: { transfer_id: transferId, is_pallet: isPallet, packing_values: packingValues },
  });
  return data.pdfBase64;
}

export async function fetchTransferUpsLabelData(transferId: number): Promise<UpsLabelData> {
  const data = await apiFetch<{ data: UpsLabelData }>(`/api/shipping/transfers/${String(transferId)}/ups-label-data`);
  return data.data;
}

export async function createTransferUpsShipment(
  transferId: number,
  packages: Array<{ length: number; width: number; height: number; weight: number }>,
): Promise<{ tracking: string; labels: LabelImage[] }> {
  return apiFetch(`/api/shipping/transfers/${String(transferId)}/ups-shipment`, {
    method: "POST",
    body: { service_code: "03", packages },
  });
}

export async function voidTransferUpsShipment(transferId: number, trackingNo: string): Promise<void> {
  await apiFetch("/api/shipping/transfers/ups-shipment/void", {
    method: "POST",
    body: { transfer_id: transferId, tracking_no: trackingNo },
  });
}

export async function fetchTransfers(): Promise<TransferRow[]> {
  const data = await apiFetch<{ transfers: TransferRow[] }>("/api/shipping/transfers");
  return data.transfers;
}

export async function fetchPackingSlipRows(shipmentId: number): Promise<PackingSlipRowsResponse> {
  return apiFetch<PackingSlipRowsResponse>(`/api/shipping/shipments/${shipmentId}/packing-slip-rows`);
}

export async function generatePackingSlipPdf(
  shipmentId: number,
  isPallet: boolean,
  packingValues: PackingValue[],
): Promise<string> {
  const data = await apiFetch<{ pdfBase64: string }>("/api/shipping/packing-slip-pdf", {
    method: "POST",
    body: { shipment_id: shipmentId, is_pallet: isPallet, packing_values: packingValues },
  });
  return data.pdfBase64;
}

export async function generateCommercialInvoicePdf(
  shipmentId: number,
): Promise<{ pdfBase64: string; filename: string }> {
  return apiFetch<{ pdfBase64: string; filename: string }>(
    `/api/shipping/shipments/${String(shipmentId)}/commercial-invoice-pdf`,
  );
}

export async function fetchShipmentPackageDetails(
  shipmentId: number,
): Promise<{ packages: ShipmentPackageDetail[] }> {
  return apiFetch<{ packages: ShipmentPackageDetail[] }>(
    `/api/shipping/shipments/${String(shipmentId)}/package-details`,
  );
}

export async function saveShipmentPackageDetails(
  shipmentId: number,
  packages: Array<{
    box_number: number;
    length_in: number;
    width_in: number;
    height_in: number;
    weight_lbs: number;
  }>,
): Promise<{ packages: ShipmentPackageDetail[] }> {
  return apiFetch<{ packages: ShipmentPackageDetail[] }>(
    `/api/shipping/shipments/${String(shipmentId)}/package-details`,
    {
      method: "PUT",
      body: { packages },
    },
  );
}

export async function fetchUpsLabelData(shipmentId: number): Promise<UpsLabelData> {
  const data = await apiFetch<{ data: UpsLabelData }>(`/api/shipping/shipments/${shipmentId}/ups-label-data`);
  return data.data;
}

export async function createUpsShipment(
  shipmentId: number,
  packages: Array<{ length: number; width: number; height: number; weight: number }>,
): Promise<{ tracking: string; labels: LabelImage[] }> {
  return apiFetch(`/api/shipping/shipments/${shipmentId}/ups-shipment`, {
    method: "POST",
    body: { service_code: "03", packages },
  });
}

export async function fetchUpsLabels(trackingNo: string): Promise<LabelImage[]> {
  const data = await apiFetch<{ labels: LabelImage[] }>(`/api/shipping/ups-label/${encodeURIComponent(trackingNo)}`);
  return data.labels;
}

export async function voidUpsShipment(shipmentId: number, trackingNo: string): Promise<void> {
  await apiFetch("/api/shipping/ups-shipment/void", {
    method: "POST",
    body: { shipment_id: shipmentId, tracking_no: trackingNo },
  });
}

export type ZplPrinterListPayload = {
  printers: ZplPrinter[];
  printerListSource: "print-server" | "default" | "registry";
};

export { fetchZplPrinters } from "@/lib/zplPrinter";

export async function printZplLabel(trackingNo: string, printer: string): Promise<void> {
  await apiFetch("/api/shipping/print-zpl", {
    method: "POST",
    body: { tracking_no: trackingNo, printer },
  });
}

export interface LaborSessionResult {
  idle_timeout_ms: number;
  heartbeat_interval_ms: number;
  session: LaborSession | null;
  scans: LaborScan[];
  was_stale: boolean;
  active_on_other_page: string | null;
}

export interface LaborSession {
  id: number;
  started_at: string;
  device_count: number;
  net_seconds: number;
}

export interface LaborScan {
  device_serial: string;
  session_id: number;
}

export interface LaborStartResult {
  session: LaborSession | null;
  error?: string;
  page?: string;
}

export interface LaborEndResult {
  device_count?: number;
  net_seconds?: number;
  error?: string;
}

export async function fetchLaborSession(): Promise<LaborSessionResult | null> {
  return apiFetch<LaborSessionResult | null>("/api/labor/active-session", {
    query: { page: SHIPPING_LABOR_PAGE, domain: "shipping" },
  });
}

export async function startLaborSession(): Promise<LaborStartResult> {
  return apiFetch<LaborStartResult>("/api/labor/start-session", {
    method: "POST",
    body: { page: SHIPPING_LABOR_PAGE, domain: "shipping" },
  });
}

export async function endLaborSession(sessionId: number, idleTimeout: boolean): Promise<LaborEndResult | null> {
  return apiFetch<LaborEndResult | null>("/api/labor/end-session", {
    method: "POST",
    body: { sessionId, idleTimeout, domain: "shipping" },
  });
}

export async function sendLaborHeartbeat(sessionId: number): Promise<void> {
  await apiFetch("/api/labor/heartbeat", {
    method: "POST",
    body: { sessionId, domain: "shipping" },
  });
}

export async function recordLaborShipment(sessionId: number, shipmentId: number): Promise<void> {
  await apiFetch("/api/labor/record-scan", {
    method: "POST",
    body: {
      sessionId,
      deviceSerial: `shipment:${shipmentId}`,
      target: { kind: "shipment", id: shipmentId },
      domain: "shipping",
    },
  });
}
